import { useEffect, useState } from 'react'
import { Button } from '@/ui'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { saveAs } from 'file-saver'
import { uniq } from 'lodash'

import { adminDestroyUserDocument } from '@/actions/admin/userDocumentsActions'
import { downloadZipDocuments } from './helpers'
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'
import { UserWithAdminInfo } from '@/reducers/admin/allUsersReducer'
import AdminDocumentTableWrapper from './AdminDocumentTableWrapper'
import { Modal } from '@/componentsV1/BaseComponents'
import { useReselector } from '@/utils/sharedHooks'

import { selectUserDocumentsByType } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchTaxUserDocumentsIfNeededForUser } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'
import { faUpload, faTrash, faDownload } from '@fortawesome/pro-solid-svg-icons'
interface Props {
  user: UserWithAdminInfo
  documentType: UploadDocumentType
}

const DocumentsTable = ({ user, documentType }: Props) => {
  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false)
  const [selectedDocs, setSelectedDocs] = useState<number[]>([])
  const [downloadingDocuments, setDownloadingDocuments] = useState(false)
  const [deletingDocuments, setDeletingDocuments] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const dispatch = useAppDispatch()
  const userDocuments = useReselector(
    selectUserDocumentsByType,
    user.id,
    documentType
  )

  /* If user  changes, clear selected*/
  useEffect(() => setSelectedDocs([]), [user.id])
  useEffect(() => {
    if (documentType === UploadDocumentType.TAX) {
      dispatch(fetchTaxUserDocumentsIfNeededForUser(user.id))
    }
  }, [user.id, dispatch, documentType])

  const downloadDocuments = async () => {
    setDownloadingDocuments(true)
    // Fetch ZIP from back end, based on documents
    const zipName = `${user.firstName}-${user.lastName}-${DateTime.now().toFormat(DATE_FORMATS_LUXON.INPUT_SHORT_YEAR)}.zip`
    const result = await downloadZipDocuments(selectedDocs)
    if (result) {
      // Generate Blob of the returned Zip
      const blob = new Blob([result.data], { type: 'application/zip' })
      // Initiate a save
      saveAs(blob, zipName)
      setDownloadingDocuments(false)
    }
  }

  const deleteDocuments = async () => {
    setConfirmDeleteOpen(false)
    setDeletingDocuments(true)
    await Promise.all(
      selectedDocs.map((id) => adminDestroyUserDocument(id)(dispatch))
    )
    setDeletingDocuments(false)
    setSelectedDocs([])
  }

  const onCheck = (docId: number, checked: boolean) =>
    setSelectedDocs((docs) => {
      return checked
        ? uniq([...docs, docId])
        : docs.filter((id) => id !== docId)
    })

  return (
    <div className="w-full">
      {documentType !== UploadDocumentType.RECEIPT && (
        <div className="flex flex-wrap items-start">
          <div className="w-full md:w-1/2 lg:w-1/2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setUploadDocumentModalOpen(true)}
              iconLeft={faUpload}
            >
              Upload File
            </Button>
          </div>

          <div className="w-full md:w-1/2 lg:w-1/2 flex gap-2 justify-end">
            <Button
              variant="secondary"
              iconLeft={faTrash}
              size="sm"
              onClick={() => setConfirmDeleteOpen(true)}
              loading={deletingDocuments}
              disabled={!selectedDocs.length}
            >
              Delete Documents
            </Button>
            <Button
              onClick={downloadDocuments}
              loading={downloadingDocuments}
              disabled={!selectedDocs.length}
              variant="secondary"
              iconLeft={faDownload}
              size="sm"
            >
              Download Selected Files in Zip
            </Button>
          </div>
        </div>
      )}

      <FileUploadModal
        open={uploadDocumentModalOpen}
        close={() => setUploadDocumentModalOpen(false)}
        user={user}
        userFacing={false}
        documentType={documentType}
      />
      <AdminDocumentTableWrapper
        documentType={documentType}
        documents={Object.values(userDocuments)}
        onCheck={onCheck}
      />

      <Modal size="mini" open={confirmDeleteOpen}>
        <Modal.Header>Delete documents</Modal.Header>
        <Modal.Content>
          Are you sure you want to delete these documents?
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmDeleteOpen(false)} variant="primary">
            Cancel
          </Button>
          <Button onClick={deleteDocuments} variant="danger" size="sm">
            Delete documents
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

export default DocumentsTable
